import { Injectable, OnDestroy } from "@angular/core";
import { RouteConfigLoadEnd, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { ObgConfigFeAdaptiveClientCommon } from "@obg/common.config";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import { Jurisdiction, JurisdictionSelector, MarketSelector } from "@obg/ngx.core";
import { ImageContent, ImageSelector, TranslationService, TranslationOptions } from "@obg/ngx.core.content";
import { ConfigService } from "@obg/ngx.global";
import { CookieService, DocumentRef, PlatformService, TelemetryService } from "@obg/ngx.global.evo";
import CSS from "csstype";
import { addYears } from "date-fns";
import { Observable, fromEvent, Subscription, EMPTY, combineLatest } from "rxjs";
import { delay, filter, mapTo, mergeMap, take, tap, withLatestFrom } from "rxjs/operators";

import {
	AGE_VERIFICATION_DIALOG_CACHE_KEY,
	GENERAL_BRAND_LOGO,
	GENERAL_PREFIX,
	HEADER_SLOT_NAME,
	OBG_EXPERIMENTS_SOURCE,
	OBG_EXPERIMENT_VALUE,
	SECURITY_18_IMAGE,
	SPONSORSHIP_IMAGE_NAME_WITHOUT_JURISDICTION,
	TITLE_ICON_SLOT_NAME,
	WDS_DIALOG_ACTION_EVENT,
	WDS_DIALOG_SECONDARY_ACTION_NAME,
	WDS_DIALOG_SELECTOR,
	headerBrandLogoStyleDictionary,
	headerSponsorshipLogoStyleDictionary,
	headerStyleDictionary,
	headerTitleImageLogoStyleDictionary,
	jurisdictionPropertyDictionary,
	separatorStyleDictionary,
	translationCrsKeys,
} from "./age-verification-dialog.const";
import { WdsDialogProperties, WdsDialogTranslatableProperties } from "./age-verification-dialog.model";

@Injectable({ providedIn: "root" })
export class AgeVerificationDialogService implements OnDestroy {
	readonly GTM_FormName_Subarea = "AgeVerification_Notification";

	// temporary solution until WebComponents types won't be available in Angular
	private _dialogWebComponent: any;
	private _subscription$ = new Subscription();

	constructor(
		private store: Store,
		private router: Router,
		private documentRef: DocumentRef,
		private imageSelector: ImageSelector,
		private marketSelector: MarketSelector,
		private translationService: TranslationService,
		private telemetryService: TelemetryService,
		private cookieService: CookieService,
		private platformService: PlatformService,
		private service: ConfigService<ObgConfigFeAdaptiveClientCommon>
	) {}

	initialize$(): Observable<void> {
		return combineLatest([
			this.store.select(JurisdictionSelector.get),
			this.store.select(this.marketSelector.getLanguageCode()),
		]).pipe(
			delay(0),
			mergeMap(([jurisdiction, market]: [Jurisdiction, string]) => {
				if (
					(this.getAgeVerificationDialogJurisdictions(jurisdiction) ||
						this.getAgeVerificationDialogMarkets(market)) &&
					!this.isEnabledToShowDialogModal() &&
					this.platformService.isBrowser
				) {
					return this.translationService
						.translateByJurisdictionPrefixList$(
							translationCrsKeys[jurisdiction] ?? ({} as TranslationOptions)
						)
						.pipe(
							withLatestFrom(
								this.store.select(this.imageSelector.getByKey(GENERAL_PREFIX, GENERAL_BRAND_LOGO)),
								this.store.select(
									this.imageSelector.getByKey(
										GENERAL_PREFIX,
										`${SPONSORSHIP_IMAGE_NAME_WITHOUT_JURISDICTION}-${jurisdiction.toLowerCase()}`
									)
								),
								this.store.select(this.imageSelector.getByKey(GENERAL_PREFIX, SECURITY_18_IMAGE))
							),
							tap(([translations, brandLogo, sponsorshipImage, securityImage]) =>
								this.showAgeVerificationDialog(
									jurisdiction,
									translations,
									brandLogo,
									sponsorshipImage,
									securityImage
								)
							),
							mapTo(undefined)
						);
				}

				return EMPTY;
			})
		);
	}

	ngOnDestroy(): void {
		this._subscription$.unsubscribe();
	}

	showAgeVerificationDialog(
		jurisdiction: Jurisdiction,
		translations?: (string | undefined)[],
		brandLogo?: ImageContent,
		sponsorshipImage?: ImageContent,
		securityImage?: ImageContent
	) {
		if (this._dialogWebComponent) {
			return;
		}

		const properties = jurisdictionPropertyDictionary[jurisdiction];

		if (!properties) {
			return;
		}

		this._dialogWebComponent = document.createElement(WDS_DIALOG_SELECTOR);

		const translationObj: WdsDialogTranslatableProperties = {};
		[
			translationObj.dialogTitle,
			translationObj.secondaryTitle,
			translationObj.description,
			translationObj.fallbackPrimaryButtonText,
			translationObj.fallbackSecondaryButtonText,
		] = translations!;

		Object.entries(properties).forEach(([key, value]) => {
			this._dialogWebComponent[key] = translationObj[key as keyof WdsDialogProperties] ?? value;
		});

		this.documentRef.native.body.appendChild(this._dialogWebComponent);
		this.documentRef.native.body.style.overflow = "hidden";

		this.createSlottedHeaderElement(jurisdiction, brandLogo, sponsorshipImage);

		this.createSlottedTitleImageElement(jurisdiction, securityImage);

		this.initActionButtonListener();

		const routeEnd$ = this.router.events.pipe(
			filter((event) => event instanceof RouteConfigLoadEnd),
			take(1),
			tap(() => {
				setTimeout(() => {
					this.telemetryService.track({
						event: GtmEventAction.GenericFormOpen,
						FormName: this.GTM_FormName_Subarea,
						SubArea: this.GTM_FormName_Subarea,
					});
				}, 2000);
			})
		);

		this._subscription$.add(routeEnd$.subscribe());
	}

	removeAgeVerificationDialog() {
		if (!this._dialogWebComponent) {
			return;
		}

		this.documentRef.native.body.removeChild(this._dialogWebComponent);
		this.documentRef.native.body.style.overflow = "";
	}

	private createSlottedHeaderElement(
		jurisdiction: Jurisdiction,
		logoContent?: ImageContent,
		sponsorshipImage?: ImageContent
	) {
		const headerStyle = headerStyleDictionary[jurisdiction];

		if (!headerStyle || !logoContent?.url) {
			return;
		}

		const header = this.createDivElement(headerStyle, HEADER_SLOT_NAME);

		const brandLogo = this.createImageElement(logoContent, headerBrandLogoStyleDictionary[jurisdiction!]);
		header.append(brandLogo);

		this._dialogWebComponent.append(header);

		if (!sponsorshipImage?.url) {
			return;
		}

		const separatorStyle = separatorStyleDictionary[jurisdiction!];

		if (!separatorStyle) {
			return;
		}

		const separator = this.createDivElement(separatorStyle);
		header.append(separator);

		const sponsorshipLogoStyle = headerSponsorshipLogoStyleDictionary[jurisdiction!];
		const sponsorshipLogo = this.createImageElement(sponsorshipImage, sponsorshipLogoStyle);

		header.append(sponsorshipLogo);
	}

	private createSlottedTitleImageElement(jurisdiction: Jurisdiction, securityImage?: ImageContent) {
		if (!securityImage?.url) {
			return;
		}

		const titleImageStyle = headerTitleImageLogoStyleDictionary[jurisdiction];
		const img = this.createImageElement(securityImage, titleImageStyle, TITLE_ICON_SLOT_NAME);

		this._dialogWebComponent.append(img);
	}

	private initActionButtonListener() {
		const actionButtonEventListener$ = fromEvent(this._dialogWebComponent, WDS_DIALOG_ACTION_EVENT).pipe(
			tap(({ detail }: CustomEvent) => {
				if (detail === WDS_DIALOG_SECONDARY_ACTION_NAME) {
					this.removeAgeVerificationDialog();
					this.writeCacheValue();

					this.telemetryService.track({
						event: GtmEventAction.GenericButtonClick,
						FormName: this.GTM_FormName_Subarea,
						ItemClicked: "Yes",
						SubArea: this.GTM_FormName_Subarea,
					});
				}
			})
		);

		this._subscription$.add(actionButtonEventListener$.subscribe());
	}

	private readCachedValue(): { accepted: Date; expiryDate: Date } | undefined {
		return this.cookieService.getJson(AGE_VERIFICATION_DIALOG_CACHE_KEY);
	}

	private writeCacheValue() {
		const now = new Date();
		const expiryDate = addYears(now, 1);
		const name = AGE_VERIFICATION_DIALOG_CACHE_KEY;
		this.cookieService.setJson({
			expiryDate,
			name,
			value: { accepted: now },
		});
	}

	private isEnabledToShowDialogModal(): boolean {
		return !!this.readCachedValue();
	}

	private getAgeVerificationDialogJurisdictions(jurisdiction: Jurisdiction): boolean {
		const experiments = this.cookieService.get(OBG_EXPERIMENTS_SOURCE);
		const isDialogEnabled = !experiments?.toLowerCase().includes(OBG_EXPERIMENT_VALUE);

		if (!jurisdiction || !isDialogEnabled) {
			return false;
		}

		const enabledJurisdictions = this.service.get(
			(config) => config.common.ageVerificationDialog.enabledJurisdictions as Jurisdiction[],
			[]
		);

		return enabledJurisdictions.includes(jurisdiction);
	}

	private getAgeVerificationDialogMarkets(market: string): boolean {
		const enabledMarkets = this.service.get((config) => config.common.ageVerificationDialog.enabledMarkets, []);

		return enabledMarkets.includes(market);
	}

	private createDivElement(style: CSS.Properties, slotName?: string): HTMLElement {
		const div = this.documentRef.native.createElement("div");

		Object.entries(style).forEach(([key, value]) => {
			(div.style as any)[key] = value;
		});

		if (slotName) {
			div.slot = slotName;
		}

		return div;
	}

	private createImageElement(
		imageContent: ImageContent,
		imageStyle?: CSS.Properties,
		slotName?: string
	): HTMLElement {
		const img = this.documentRef.native.createElement("img");

		img.src = imageContent.url;
		img.alt = imageContent.altText ?? "";
		img.style.width = (imageStyle?.width as string) ?? `${imageContent.width}px`;
		img.style.height = (imageStyle?.height as string) ?? `${imageContent.height}px`;
		if (imageStyle?.display) {
			img.style.display = imageStyle.display as string;
		}

		if (slotName) {
			img.slot = slotName;
		}

		return img;
	}
}
